import { generateClient } from "aws-amplify/api";
import { fetchUserAttributes } from "aws-amplify/auth";
import { useState } from "react";
import { FaSyncAlt } from "react-icons/fa";
import {
  createNotifications,
  updateNotifications,
} from "../graphql/mutations.js";
import { listOrganizationCPs } from "../graphql/queries.js";
import { assessmentQuestionLabelByKey } from "./Assessment.js";

const client = generateClient();

function formatRecommendationDate(dateValue) {
  if (!dateValue) return "N/A";

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return dateValue;
  }

  return parsed.toLocaleDateString();
}

function formatAssessmentQuestion(resultKey) {
  if (!resultKey) return "N/A";
  return assessmentQuestionLabelByKey?.[resultKey] || resultKey;
}

function renderRecommendationDetails(note) {
  return (
    <div style={detailsStyle}>
      <div>
        <strong>Date:</strong>{" "}
        {formatRecommendationDate(note.RecommendationDate)}
      </div>
      <div>
        <strong>Type:</strong> {note.RecommendationType || "N/A"}
      </div>
      <div>
        <strong>Action:</strong> {note.RecommendationAction || "N/A"}
      </div>
      <div>
        <strong>Frequency:</strong>{" "}
        {note.RecommendationActionFrequency || "N/A"}
      </div>
      <div>
        <strong>Assessment Question:</strong>{" "}
        {formatAssessmentQuestion(note.RecommendationResult)}
      </div>
      <div>
        <strong>Note:</strong> {note.RecommendationNote || "N/A"}
      </div>
    </div>
  );
}

export default function NotificationsPopup({
  visible,
  onClose,
  recommendations,
  generalNotifications,
  onRefresh,
  loading,
}) {
  const [activeTab, setActiveTab] = useState("recommendations"); // "recommendations" or "general"

  async function handleDismiss(note) {
    try {
      await client.graphql({
        authMode: "userPool",
        query: updateNotifications,
        variables: {
          input: {
            id: note.id,
            Status: "read",
            StatusDate: new Date().toISOString().split("T")[0],
          },
        },
      });

      if (typeof onRefresh === "function") {
        await onRefresh(false);
      }
    } catch (err) {
      console.error("Error dismissing notification:", err);
    }
  }

  async function handleResponse(note, response) {
    try {
      // Update original notification status
      const updateRes = await client.graphql({
        authMode: "userPool",
        query: updateNotifications,
        variables: {
          input: {
            id: note.id,
            Status: response,
            StatusDate: new Date().toISOString().split("T")[0],
          },
        },
      });
      console.log("updateNotifications response:", updateRes);

      const userAttrs = await fetchUserAttributes();
      const responderId = userAttrs.sub;
      const responderFirstName = userAttrs["given_name"] || "";
      const responderLastName = userAttrs["family_name"] || "";
      const responderEmail = String(userAttrs.email || "")
        .trim()
        .toLowerCase();
      const today = new Date().toISOString().split("T")[0];

      let organizationId = note?.organizationId || null;
      if (!organizationId) {
        try {
          const proEmail = String(note?.SenderEmail || "")
            .trim()
            .toLowerCase();
          if (proEmail) {
            const orgCPRes = await client.graphql({
              authMode: "userPool",
              query: listOrganizationCPs,
              variables: {
                filter: { OrganizationCPEmail: { eq: proEmail } },
                limit: 1,
              },
            });
            organizationId =
              orgCPRes?.data?.listOrganizationCPs?.items?.[0]?.OrganizationId ||
              null;
          }
        } catch (e) {
          organizationId = null;
        }
      }

      // Create RecommendationBack notification (must match schema field names)
      const newNote = {
        RecommendationResult: note?.RecommendationResult || null,
        RecommendationAction: note?.RecommendationAction || null,
        RecommendationActionFrequency:
          note?.RecommendationActionFrequency || null,
        RecommendationType: note?.RecommendationType || null,
        RecommendationNote: response === "accepted" ? "Accepted" : "Declined",
        RecommendationDate: today,
        SenderId: responderId,
        SenderFirstName: responderFirstName,
        SenderLastName: responderLastName,
        SenderEmail: responderEmail,
        TargetEmail: String(note?.SenderEmail || "")
          .trim()
          .toLowerCase(),
        TargetUserId: note?.SenderId || null,
        NotificationType: "RecommendationBack",
        Status: "pending",
        StatusDate: today,
        ...(organizationId ? { organizationId } : {}),
      };

      const createRes = await client.graphql({
        authMode: "userPool",
        query: createNotifications,
        variables: { input: newNote },
      });
      console.log("createNotifications input:", newNote);
      console.log("createNotifications response:", createRes);

      if (typeof onRefresh === "function") {
        await onRefresh(false);
      }
    } catch (err) {
      console.error("Error responding to recommendation:", err);
    }
  }

  if (!visible) return null;

  return (
    <div style={popupStyle}>
      <div style={headerStyle}>
        <h3 style={{ margin: 0 }}>Notifications</h3>
        <button
          aria-label="Refresh notifications"
          disabled={loading}
          onClick={() => {
            if (typeof onRefresh === "function") {
              onRefresh(true);
            }
          }}
          style={{
            ...refreshBtnStyle,
            ...(loading ? refreshBtnDisabledStyle : {}),
          }}
          title="Refresh notifications"
        >
          <FaSyncAlt
            style={loading ? { animation: "spin 1s linear infinite" } : {}}
          />
        </button>
      </div>

      <div style={{ display: "flex", marginBottom: 10 }}>
        <button
          style={{
            ...tabBtnStyle,
            ...(activeTab === "general" ? activeTabStyle : {}),
          }}
          onClick={() => setActiveTab("general")}
        >
          General Notifications
        </button>
        <button
          style={{
            ...tabBtnStyle,
            ...(activeTab === "recommendations" ? activeTabStyle : {}),
          }}
          onClick={() => setActiveTab("recommendations")}
        >
          Recommendations
        </button>
      </div>

      <div style={scrollAreaStyle}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <div
              style={{
                width: 32,
                height: 32,
                border: "4px solid #ccc",
                borderTop: "4px solid #0077cc",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto",
              }}
            />
            <p style={{ marginTop: 10 }}>Loading...</p>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : activeTab === "recommendations" ? (
          recommendations.length === 0 ? (
            <p>No recommendations found.</p>
          ) : (
            recommendations.map((note) => (
              <div key={note.id} style={cardStyle}>
                <div>
                  <strong>From:</strong> {note.SenderFirstName}{" "}
                  {note.SenderLastName} ({note.SenderEmail})
                </div>

                {note.NotificationType === "RecommendationBack" ? (
                  <>
                    <div style={{ marginTop: 8 }}>
                      <span
                        style={{
                          fontWeight: "bold",
                          color:
                            note.RecommendationNote === "Accepted"
                              ? "green"
                              : "red",
                          fontSize: 16,
                        }}
                      >
                        {note.RecommendationNote}
                      </span>{" "}
                      — {note.SenderEmail} ({note.SenderFirstName}{" "}
                      {note.SenderLastName}){" "}
                      {note.RecommendationNote.toLowerCase()}ed this action.
                    </div>
                    {renderRecommendationDetails(note)}
                    <div style={{ marginTop: 8 }}>
                      <button
                        style={closeBtn}
                        onClick={() => handleDismiss(note)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ marginTop: 8 }}>
                      {(() => {
                        switch (note.RecommendationType) {
                          case "Recommend Changing Frequency":
                            return (
                              <>
                                Your professional recommends that you change the
                                frequency for "
                                <strong>{note.RecommendationAction}</strong>" to{" "}
                                <strong>
                                  {note.RecommendationActionFrequency ||
                                    "Nothing"}
                                </strong>
                                .
                              </>
                            );
                          case "Recommend Adding New Category":
                            return (
                              <>
                                Your professional recommends you create a new
                                plan section:{" "}
                                <strong>{note.RecommendationResult}</strong> and
                                add <strong>{note.RecommendationAction}</strong>{" "}
                                to it.
                              </>
                            );
                          case "Recommend Discontinuing Action":
                            return (
                              <>
                                Your professional recommends you discontinue "
                                <strong>{note.RecommendationAction}</strong>"
                                under{" "}
                                <strong>{note.RecommendationResult}</strong>.
                              </>
                            );
                          case "Making Good Progress, No Change Recommended":
                            return (
                              <>
                                Your professional says you are making good
                                progress with "
                                <strong>{note.RecommendationAction}</strong>"
                                under{" "}
                                <strong>{note.RecommendationResult}</strong>. No
                                change recommended.
                              </>
                            );
                          case "Having Some Challenges. Reassess Action Later":
                            return (
                              <>
                                Your professional says you are having some
                                challenges with "
                                <strong>{note.RecommendationAction}</strong>"
                                under{" "}
                                <strong>{note.RecommendationResult}</strong>
                                {note.RecommendationActionFrequency
                                  ? ` at a frequency of ${note.RecommendationActionFrequency}`
                                  : ""}
                                . Reassess this action later.
                              </>
                            );
                          case "Recommend Add New Action":
                            return (
                              <>
                                Your professional recommends adding "
                                <strong>{note.RecommendationAction}</strong>" to
                                <strong>{note.RecommendationResult}</strong>
                                {note.RecommendationActionFrequency
                                  ? ` with a frequency of ${note.RecommendationActionFrequency}`
                                  : ""}
                                .
                              </>
                            );
                          default:
                            return null;
                        }
                      })()}
                    </div>

                    {note.RecommendationNote && (
                      <div style={{ marginTop: 10 }}>
                        <strong>Additional Notes:</strong>
                        <br />
                        {note.RecommendationNote}
                      </div>
                    )}

                    {renderRecommendationDetails(note)}

                    <div style={{ marginTop: 8 }}>
                      <button
                        style={acceptBtn}
                        onClick={() => handleResponse(note, "accepted")}
                      >
                        Accept
                      </button>
                      <button
                        style={declineBtn}
                        onClick={() => handleResponse(note, "declined")}
                      >
                        Decline
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )
        ) : activeTab === "general" ? (
          generalNotifications.length === 0 ? (
            <p>No notifications found.</p>
          ) : (
            generalNotifications.map((note) => {
              // Determine message based on NotificationType
              let message = "";
              switch (note.NotificationType) {
                case "ReviewReady":
                  message = `(${note.SenderEmail}) ${note.RecommendationNote}.`;
                  break;
                case "ConnectionResponse":
                  message = `${note.SenderFirstName} ${note.SenderLastName} (${note.SenderEmail}) ${note.RecommendationNote} your connection request.`;
                  break;
                case "Reviewed":
                  message = `(${note.SenderEmail}) ${note.RecommendationNote}.`;
                  break;
                case "CommunityHideEscalation1":
                  message =
                    note.RecommendationNote ||
                    "Several community members have hidden your posts. Please reconsider what you are sharing.";
                  break;
                case "CommunityHideEscalation2":
                  message =
                    note.RecommendationNote ||
                    "Community members continue to hide your posts. You may lose access if this continues.";
                  break;
                case "CommunityHideEscalation3":
                  message =
                    note.RecommendationNote ||
                    "You have reached a final moderation warning for hidden posts.";
                  break;
                case "CommunityAccessRevoked":
                  message =
                    note.RecommendationNote ||
                    "Your access to this community has been removed.";
                  break;
                case "CommunityUnsafeConcern":
                  message =
                    note.RecommendationNote ||
                    "Others are concerned for your safety. Please contact a trusted friend, family member, pastor, or 988.";
                  break;
                default:
                  message = note.RecommendationNote || "Notification";
                  break;
              }

              return (
                <div key={note.id} style={cardStyle}>
                  <div>{message}</div>
                  <div style={{ marginTop: 8 }}>
                    <button
                      style={closeBtn}
                      onClick={() => handleDismiss(note)}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              );
            })
          )
        ) : null}
      </div>

      <button style={closeBtn} onClick={onClose}>
        Close
      </button>
    </div>
  );
}

// --- Styles ---
const popupStyle = {
  position: "fixed",
  top: 80,
  right: 20,
  backgroundColor: "white",
  border: "1px solid #ccc",
  borderRadius: 12,
  padding: 20,
  boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  width: 340,
  zIndex: 2000,
  display: "flex",
  flexDirection: "column",
  maxHeight: "70vh",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 12,
};

const scrollAreaStyle = {
  overflowY: "auto",
  flex: 1,
  paddingRight: 4,
};

const cardStyle = {
  padding: 12,
  marginBottom: 10,
  border: "1px solid #ddd",
  borderRadius: 8,
  backgroundColor: "#f9f9f9",
};

const detailsStyle = {
  marginTop: 10,
  padding: 10,
  borderRadius: 6,
  backgroundColor: "#eef5fb",
  display: "grid",
  gap: 4,
  fontSize: 13,
};

const acceptBtn = {
  backgroundColor: "#2F5D50",
  color: "white",
  border: "none",
  padding: "6px 12px",
  marginRight: 8,
  borderRadius: 6,
  cursor: "pointer",
};

const declineBtn = {
  backgroundColor: "#8C1456",
  color: "white",
  border: "none",
  padding: "6px 12px",
  borderRadius: 6,
  cursor: "pointer",
};

const closeBtn = {
  marginTop: 12,
  backgroundColor: "#0067B3",
  color: "white",
  border: "none",
  padding: "8px 12px",
  borderRadius: 6,
  cursor: "pointer",
  width: "100%",
};

const refreshBtnStyle = {
  alignItems: "center",
  backgroundColor: "transparent",
  border: "none",
  color: "#4d4d4d",
  cursor: "pointer",
  display: "inline-flex",
  fontSize: 14,
  justifyContent: "center",
  padding: 6,
};

const refreshBtnDisabledStyle = {
  cursor: "default",
  opacity: 0.5,
};

const tabBtnStyle = {
  flex: 1,
  padding: "8px 0",
  cursor: "pointer",
  background: "#eee",
  border: "none",
  borderRadius: "6px 6px 0 0",
  fontWeight: "600",
};

const activeTabStyle = {
  background: "#0067B3",
  color: "white",
};
